// Vital Signs Normal Ranges and Validation

// Module-level DB parameter cache — set by ClinicalParametersContext on load.
// This allows non-React utility callers to benefit from DB-backed ranges.
let _dbParamMap = null;

export function setDbParameterMap(paramMap) {
  _dbParamMap = paramMap;
}

export function getDbParameterMap() {
  return _dbParamMap;
}

// Default adult ranges (fallback when DB ranges are not available)
export const vitalSignsRanges = {
  temperature: { min: 36.5, max: 37.5, unit: '°C', name: 'Temperature' },
  blood_pressure_systolic: { min: 90, max: 140, unit: 'mmHg', name: 'BP Systolic' },
  blood_pressure_diastolic: { min: 60, max: 90, unit: 'mmHg', name: 'BP Diastolic' },
  heart_rate: { min: 60, max: 100, unit: 'bpm', name: 'Heart Rate' },
  respiratory_rate: { min: 12, max: 20, unit: '/min', name: 'Respiratory Rate' },
  oxygen_saturation: { min: 95, max: 100, unit: '%', name: 'O2 Saturation' },
  fetal_heart_rate: { min: 110, max: 160, unit: 'bpm', name: 'Fetal Heart Rate' },
  hemoglobin: { min: 11, max: 14, unit: 'g/dL', name: 'Hemoglobin', context: 'pregnancy' },
  blood_sugar: { min: 70, max: 140, unit: 'mg/dL', name: 'Blood Sugar' },
  weight: { min: 40, max: 200, unit: 'kg', name: 'Weight' },
  muac: { min: 22, max: 34, unit: 'cm', name: 'MUAC' },
};

// Age-based anthropometric reference ranges (focused on pediatric clinical use)
const getAnthropometricRanges = (age) => {
  const normalizedAge = Number(age);
  if (age === null || age === undefined || Number.isNaN(normalizedAge)) return {};

  // Neonate (0-28 days)
  if (normalizedAge < 0.08) {
    return {
      head_circumference: { min: 32, max: 38, unit: 'cm', name: 'Head Circumference' },
    };
  }

  // Infant (1 month - 1 year)
  if (normalizedAge < 1) {
    return {
      muac: { min: 11, max: 15, unit: 'cm', name: 'MUAC' },
      head_circumference: { min: 36, max: 48, unit: 'cm', name: 'Head Circumference' },
    };
  }

  // Toddler (1-3 years)
  if (normalizedAge < 3) {
    return {
      muac: { min: 12.5, max: 17.5, unit: 'cm', name: 'MUAC' },
      head_circumference: { min: 45, max: 50, unit: 'cm', name: 'Head Circumference' },
    };
  }

  // Preschool (3-6 years)
  if (normalizedAge < 6) {
    return {
      muac: { min: 13.0, max: 19.0, unit: 'cm', name: 'MUAC' },
      head_circumference: { min: 47, max: 52, unit: 'cm', name: 'Head Circumference' },
    };
  }

  // School age (6-10 years)
  if (normalizedAge < 10) {
    return {
      muac: { min: 14.0, max: 21.0, unit: 'cm', name: 'MUAC' },
    };
  }

  // Early adolescence (10-15 years)
  if (normalizedAge < 15) {
    return {
      muac: { min: 16.5, max: 25.0, unit: 'cm', name: 'MUAC' },
    };
  }

  // Late adolescence (15-18 years)
  if (normalizedAge < 18) {
    return {
      muac: { min: 19.0, max: 29.0, unit: 'cm', name: 'MUAC' },
    };
  }

  // Adults (18+ years)
  return {
    muac: { min: 22, max: 34, unit: 'cm', name: 'MUAC' },
  };
};

/**
 * Get age-specific vital signs ranges
 * @param {number} age - Patient age in years
 * @returns {object} Age-appropriate ranges for vital signs
 */
export const getAgeSpecificRanges = (age) => {
  const normalizedAge = Number(age);

  if (age === null || age === undefined || Number.isNaN(normalizedAge)) {
    return vitalSignsRanges;
  }

  // Neonate (0-28 days / <1 month)
  if (normalizedAge < 0.08) {
    return {
      temperature: { min: 36.5, max: 37.5, unit: '°C', name: 'Temperature' },
      heart_rate: { min: 100, max: 180, unit: 'bpm', name: 'Heart Rate' },
      respiratory_rate: { min: 30, max: 60, unit: '/min', name: 'Respiratory Rate' },
      blood_pressure_systolic: { min: 60, max: 90, unit: 'mmHg', name: 'BP Systolic' },
      blood_pressure_diastolic: { min: 30, max: 60, unit: 'mmHg', name: 'BP Diastolic' },
      oxygen_saturation: { min: 95, max: 100, unit: '%', name: 'O2 Saturation' },
      ...getAnthropometricRanges(normalizedAge),
    };
  }
  
  // Infant (1 month - 1 year)
  if (normalizedAge < 1) {
    return {
      temperature: { min: 36.5, max: 37.5, unit: '°C', name: 'Temperature' },
      heart_rate: { min: 100, max: 160, unit: 'bpm', name: 'Heart Rate' },
      respiratory_rate: { min: 25, max: 50, unit: '/min', name: 'Respiratory Rate' },
      blood_pressure_systolic: { min: 70, max: 100, unit: 'mmHg', name: 'BP Systolic' },
      blood_pressure_diastolic: { min: 40, max: 65, unit: 'mmHg', name: 'BP Diastolic' },
      oxygen_saturation: { min: 95, max: 100, unit: '%', name: 'O2 Saturation' },
      ...getAnthropometricRanges(normalizedAge),
    };
  }
  
  // Toddler (1-3 years)
  if (normalizedAge < 3) {
    return {
      temperature: { min: 36.5, max: 37.5, unit: '°C', name: 'Temperature' },
      heart_rate: { min: 90, max: 150, unit: 'bpm', name: 'Heart Rate' },
      respiratory_rate: { min: 20, max: 40, unit: '/min', name: 'Respiratory Rate' },
      blood_pressure_systolic: { min: 80, max: 110, unit: 'mmHg', name: 'BP Systolic' },
      blood_pressure_diastolic: { min: 50, max: 70, unit: 'mmHg', name: 'BP Diastolic' },
      oxygen_saturation: { min: 95, max: 100, unit: '%', name: 'O2 Saturation' },
      ...getAnthropometricRanges(normalizedAge),
    };
  }
  
  // Preschooler (3-6 years)
  if (normalizedAge < 6) {
    return {
      temperature: { min: 36.5, max: 37.5, unit: '°C', name: 'Temperature' },
      heart_rate: { min: 80, max: 140, unit: 'bpm', name: 'Heart Rate' },
      respiratory_rate: { min: 20, max: 30, unit: '/min', name: 'Respiratory Rate' },
      blood_pressure_systolic: { min: 85, max: 115, unit: 'mmHg', name: 'BP Systolic' },
      blood_pressure_diastolic: { min: 50, max: 75, unit: 'mmHg', name: 'BP Diastolic' },
      oxygen_saturation: { min: 95, max: 100, unit: '%', name: 'O2 Saturation' },
      ...getAnthropometricRanges(normalizedAge),
    };
  }
  
  // School Age (6-12 years)
  if (normalizedAge < 12) {
    return {
      temperature: { min: 36.5, max: 37.5, unit: '°C', name: 'Temperature' },
      heart_rate: { min: 70, max: 120, unit: 'bpm', name: 'Heart Rate' },
      respiratory_rate: { min: 18, max: 25, unit: '/min', name: 'Respiratory Rate' },
      blood_pressure_systolic: { min: 90, max: 120, unit: 'mmHg', name: 'BP Systolic' },
      blood_pressure_diastolic: { min: 55, max: 80, unit: 'mmHg', name: 'BP Diastolic' },
      oxygen_saturation: { min: 95, max: 100, unit: '%', name: 'O2 Saturation' },
      ...getAnthropometricRanges(normalizedAge),
    };
  }
  
  // Adolescent (12-18 years)
  if (normalizedAge < 18) {
    return {
      temperature: { min: 36.5, max: 37.5, unit: '°C', name: 'Temperature' },
      heart_rate: { min: 60, max: 105, unit: 'bpm', name: 'Heart Rate' },
      respiratory_rate: { min: 12, max: 20, unit: '/min', name: 'Respiratory Rate' },
      blood_pressure_systolic: { min: 90, max: 130, unit: 'mmHg', name: 'BP Systolic' },
      blood_pressure_diastolic: { min: 60, max: 85, unit: 'mmHg', name: 'BP Diastolic' },
      oxygen_saturation: { min: 95, max: 100, unit: '%', name: 'O2 Saturation' },
      ...getAnthropometricRanges(normalizedAge),
    };
  }
  
  // Adult (18+ years) - default ranges
  return {
    temperature: { min: 36.5, max: 37.5, unit: '°C', name: 'Temperature' },
    heart_rate: { min: 60, max: 100, unit: 'bpm', name: 'Heart Rate' },
    respiratory_rate: { min: 12, max: 20, unit: '/min', name: 'Respiratory Rate' },
    blood_pressure_systolic: { min: 90, max: 140, unit: 'mmHg', name: 'BP Systolic' },
    blood_pressure_diastolic: { min: 60, max: 90, unit: 'mmHg', name: 'BP Diastolic' },
    oxygen_saturation: { min: 95, max: 100, unit: '%', name: 'O2 Saturation' },
    ...getAnthropometricRanges(normalizedAge),
  };
};

/**
 * Validate a vital sign value against normal ranges
 * @param {string} fieldName - Name of the vital sign field
 * @param {number|string} value - Value to validate
 * @param {number} age - Patient age in years (optional)
 * @returns {object} { status: 'normal'|'abnormal'|'neutral', color: string, message: string }
 */
export const validateVitalSign = (fieldName, value, age = null) => {
  const numValue = parseFloat(value);
  
  // Empty or invalid values are neutral
  if (!value || value === '' || isNaN(numValue)) {
    return { status: 'neutral', color: 'gray', message: '' };
  }

  // MUAC and Head Circumference are age-dependent; avoid false positives if age is unknown
  if ((fieldName === 'muac' || fieldName === 'head_circumference') && (age === null || age === undefined || Number.isNaN(Number(age)))) {
    return { status: 'neutral', color: 'gray', message: '' };
  }

  // --- DB-backed ranges (from ClinicalParametersContext) ---
  // Map frontend vital sign field names to DB parameter keys
  const dbKeyMap = {
    blood_pressure_systolic: 'blood_pressure_systolic',
    blood_pressure_diastolic: 'blood_pressure_diastolic',
    heart_rate: 'heart_rate',
    respiratory_rate: 'respiratory_rate',
    oxygen_saturation: 'oxygen_saturation',
    temperature: 'temperature',
    blood_glucose: 'blood_glucose',
    blood_sugar: 'blood_glucose',
    pain_score: 'pain_score',
    gcs_score: 'gcs_score',
    muac: 'muac',
    capillary_refill: 'capillary_refill',
    peak_flow: 'peak_flow',
  };

  if (_dbParamMap) {
    const dbKey = dbKeyMap[fieldName] || fieldName;
    const dbParam = _dbParamMap[dbKey];
    if (dbParam && dbParam.is_active) {
      // Resolve age-specific ranges from DB
      let dbRanges = {
        normal_min: dbParam.normal_min,
        normal_max: dbParam.normal_max,
        critical_low: dbParam.critical_low,
        critical_high: dbParam.critical_high,
      };

      if (dbParam.age_specific_ranges && age !== null && age !== undefined) {
        const normAge = Number(age);
        if (!isNaN(normAge)) {
          for (const bracket of dbParam.age_specific_ranges) {
            if (bracket.age_min !== undefined && bracket.age_max !== undefined && bracket.age_min <= normAge && normAge < bracket.age_max) {
              for (const k of Object.keys(dbRanges)) {
                if (bracket[k] !== undefined && bracket[k] !== null) {
                  dbRanges[k] = bracket[k];
                }
              }
              break;
            }
          }
        }
      }

      const { normal_min, normal_max, critical_low, critical_high } = dbRanges;
      const unit = dbParam.unit || '';

      if (normal_min !== null && normal_max !== null && numValue >= normal_min && numValue <= normal_max) {
        return { status: 'normal', color: 'green', message: `Normal (${normal_min}-${normal_max} ${unit})`.trim() };
      }
      if (critical_low !== null && numValue <= critical_low) {
        return { status: 'critical', color: 'red', message: `Critical low (Normal: ${normal_min}-${normal_max} ${unit})`.trim() };
      }
      if (critical_high !== null && numValue >= critical_high) {
        return { status: 'critical', color: 'red', message: `Critical high (Normal: ${normal_min}-${normal_max} ${unit})`.trim() };
      }
      if (normal_min !== null && numValue < normal_min) {
        return { status: 'abnormal', color: 'orange', message: `Low (Normal: ${normal_min}-${normal_max} ${unit})`.trim() };
      }
      if (normal_max !== null && numValue > normal_max) {
        return { status: 'abnormal', color: 'orange', message: `High (Normal: ${normal_min}-${normal_max} ${unit})`.trim() };
      }
    }
  }

  // --- Fallback to hardcoded ranges ---
  const normalizedAge = Number(age);
  let ranges = Number.isNaN(normalizedAge) ? vitalSignsRanges : getAgeSpecificRanges(normalizedAge);
  const range = ranges[fieldName];
  
  // If no range defined for this field, return neutral
  if (!range) {
    return { status: 'neutral', color: 'gray', message: '' };
  }

  // Check if within normal range
  if (numValue >= range.min && numValue <= range.max) {
    return {
      status: 'normal',
      color: 'green',
      message: `Normal (${range.min}-${range.max} ${range.unit})`
    };
  }

  // Abnormal - either too low or too high
  let severity = 'abnormal';
  let message = '';
  
  if (numValue < range.min) {
    message = `Low (Normal: ${range.min}-${range.max} ${range.unit})`;
    severity = numValue < range.min * 0.8 ? 'critical' : 'abnormal'; // <20% below is critical
  } else {
    message = `High (Normal: ${range.min}-${range.max} ${range.unit})`;
    severity = numValue > range.max * 1.2 ? 'critical' : 'abnormal'; // >20% above is critical
  }

  return {
    status: severity,
    color: severity === 'critical' ? 'red' : 'orange',
    message
  };
};

/**
 * Get input className based on validation status
 * @param {string} fieldName - Name of the vital sign field
 * @param {number|string} value - Value to validate
 * @param {string} baseClass - Base CSS classes
 * @param {number} age - Patient age in years (optional)
 * @returns {string} Complete className string
 */
export const getVitalSignInputClass = (fieldName, value, baseClass = '', age = null) => {
  const validation = validateVitalSign(fieldName, value, age);
  
  let colorClasses = '';
  if (validation.status === 'normal') {
    colorClasses = 'border-green-500 bg-green-50 focus:ring-green-500 text-green-900';
  } else if (validation.status === 'abnormal') {
    colorClasses = 'border-orange-500 bg-orange-50 focus:ring-orange-500 text-orange-900';
  } else if (validation.status === 'critical') {
    colorClasses = 'border-red-500 bg-red-50 focus:ring-red-500 text-red-900';
  } else {
    colorClasses = 'border-gray-300 focus:ring-pink-500';
  }

  return `${baseClass} ${colorClasses}`;
};

/**
 * Calculate National Early Warning Score (NEWS2) from vital signs.
 * @param {object} vitals - { respiratory_rate, oxygen_saturation, blood_pressure_systolic, heart_rate, temperature }
 * @returns {number} Total NEWS2 score (0-20)
 */
export const calculateNEWS2 = (vitals) => {
  let score = 0;
  const rr = parseInt(vitals.respiratory_rate);
  const spo2 = parseFloat(vitals.oxygen_saturation);
  const sbp = parseInt(vitals.blood_pressure_systolic);
  const hr = parseInt(vitals.heart_rate);
  const temp = parseFloat(vitals.temperature);

  if (rr) score += rr <= 8 ? 3 : rr <= 11 ? 1 : rr <= 20 ? 0 : rr <= 24 ? 2 : 3;
  if (spo2) score += spo2 <= 91 ? 3 : spo2 <= 93 ? 2 : spo2 <= 95 ? 1 : 0;
  if (sbp) score += sbp <= 90 ? 3 : sbp <= 100 ? 2 : sbp <= 110 ? 1 : sbp <= 219 ? 0 : 3;
  if (hr) score += hr <= 40 ? 3 : hr <= 50 ? 1 : hr <= 90 ? 0 : hr <= 110 ? 1 : hr <= 130 ? 2 : 3;
  if (temp) score += temp <= 35 ? 3 : temp <= 36 ? 1 : temp <= 38 ? 0 : temp <= 39 ? 1 : 2;
  return score;
};

/**
 * Get NEWS2 risk level from score.
 * @param {number} score - NEWS2 score
 * @returns {object} { level: string, color: string }
 */
export const getNEWS2Risk = (score) => {
  if (score >= 7) return { level: 'Critical', color: 'red' };
  if (score >= 5) return { level: 'High', color: 'orange' };
  if (score >= 3) return { level: 'Medium', color: 'yellow' };
  if (score >= 1) return { level: 'Low', color: 'green' };
  return { level: 'Normal', color: 'gray' };
};

